import React, { useEffect, useState } from 'react';
import {
  SuperTable,
  ALL_TABLES,
  getDefaultRowCount,
  SkeletonTableLoading,
  getTranslatedText,
  TableContainer,
  ENTITY_MAP
} from '@nile/components';
import {
  getWidth,
  getAttributeName,
  getModuleName,
  getType,
  getAccessor
} from '@nile/reports/src/modules/DataReports/components/subpages/DataReportTable/helper';
import { TextCell } from '@nile/rmm/src/modules/scripts/components/cells';
import {
  BgCell,
  AssetAgeCell,
  SurveyTypeCell,
  CommentCell
} from '@nile/reports/src/modules/component';
import { isEmpty } from 'lodash';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import ExpandableTableComponent from './GroupedDataTable';

const Wrapper = styled.div`
  .dataReportTable {
    thead {
      background: var(--sops-table-bg);
    }
    th {
      position: relative;
      border-radius: 0;
    }
    td {
      padding-left: 20px;
    }
    .table-header-text {
      padding-left: 10px;
      display: block;
    }
    th:not(:last-child):after {
      content: '';
      height: 50%;
      width: 1px;
      position: absolute;
      right: 0;
      top: 12px;
      background-color: #dedede;
    }
  }
`;

function DataReportTable(props) {
  const {
    getReportData,
    data,
    loading,
    meta,
    previewConfig = {},
    source = null,
    entity,
    reportData
  } = props;
  const intl = useIntl();

  const defaultColumnCells = {
    PLAIN: TextCell,
    BGCOLOR: BgCell,
    AssetAge: AssetAgeCell,
    SurveyType: SurveyTypeCell,
    Comments: CommentCell
  };

  const tableName = ALL_TABLES.dataReportTable;

  const ROW_COUNT = getDefaultRowCount(tableName);

  const groupBy = !isEmpty(previewConfig)
    ? previewConfig?.groupBy
    : reportData?.groupBy;
  const isGroupBy = (groupBy && groupBy.length) || 0;

  const [actTableData, setActTableData] = useState([]);
  const [reportTableData, setReportTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const [listInfo, setListInfo] = useState({
    page: 1,
    pageSize: ROW_COUNT,
    totalCount: 100
  });

  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', header: 'Name', columns: [] }
  ]);

  const [tableColumn, setTableColumn] = useState([]);

  const transformRecord = (cols, actData) => {
    if (!isEmpty(actData) && actData?.data?.[0]?.type === 'records') {
      const { records } = actData.data[0];
      const recordsData = records.map((el) => {
        const temp = {};
        el.map((dt, idx) =>
          Object.assign(temp, { [cols?.[idx]?.accessor]: dt })
        );
        return temp;
      });
      setReportTableData(recordsData);
    }
    if (!isEmpty(actData) && actData?.data?.[0]?.type === 'groups') {
      setActTableData(actData);
    }
  };
  const constructTableData = async (cols, response = {}) => {
    const columnsArr = [];

    const metaEnt =
      (response?.data &&
        Object.entries(response?.data?.additionalData).map(([key, value]) => ({
          [key.split('.').join('')]: value
        }))) ||
      {};

    cols.map((col) => {
      columnsArr.push({
        Header: col.label || '',
        header: col.label || '',
        size: getWidth(col),
        accessorKey: getAccessor(col),
        type: col.dataReportRenderType || 'PLAIN',
        id: col.accessor,
        disableResizing: true,
        disableFilters: true,
        disableSortBy: true,
        className: 'sticky',
        editable: false,
        field: col,
        meta: metaEnt?.[0] || undefined
      });
    });

    setColumnGroup([{ Header: 'Name', header: 'Name', columns: columnsArr }]);
    await transformRecord(cols, response?.data || {});
    setListInfo((prevState) => ({
      ...prevState,
      totalCount: response?.totalCount,
      page: response?.page,
      pageSize: response?.pageSize
    }));
    setTableLoading(false);
  };

  const getColumnLabel = (attr) => {
    if (attr?.isCustom) {
      return attr?.attributePlaceHolder?.label;
    }
    if (attr?.attributePlaceHolder?.label) {
      const label = attr?.attributePlaceHolder?.label;
      switch (label) {
        case 'entityAttribute.project.owner.name':
          return getTranslatedText(intl, 'entityAttribute.requester.name');
        default:
          return getTranslatedText(intl, attr?.attributePlaceHolder?.label);
      }
    }
    return attr?.label;
  };

  const getColumns = () => {
    const selectedCol = !isEmpty(previewConfig)
      ? previewConfig?.selectAttributes
      : reportData?.selectAttributes;

    const selectedEnt = !isEmpty(previewConfig)
      ? previewConfig?.entity
      : entity;

    let selectedColumns = selectedCol || [];

    const findColumn = (col) => {
      let module = selectedEnt.toLowerCase();
      let obj = col;
      if (col.includes('.')) {
        module = col.split('.').slice(-2, -1)[0];
        obj = col.split('.').slice(-1)[0];
      }
      const moduleName = getModuleName(module, entity);
      const metaAttr = meta.filter(
        (el) => el.name === ENTITY_MAP[moduleName]
      )?.[0]?.attributes;
      const columnAttr = (metaAttr &&
        metaAttr.find((el) => el.name === obj)) || {
        name: col,
        id: col,
        label: obj?.split('.').join(' '),
        dataReportRenderType: getType(col)
      };
      return { ...columnAttr, accessor: col.split('.').join('') };
    };

    const filteredAttr = selectedColumns.map((col) => findColumn(col));

    selectedColumns = filteredAttr.map((el) => ({
      name: el?.accessor,
      id: el?.name,
      label: getColumnLabel(el),
      dataReportRenderType: getType(el),
      accessor: el?.accessor
    }));

    return selectedColumns;
  };

  useEffect(() => {
    if (data && !isEmpty(meta)) {
      setTableLoading(true);
      const columns = getColumns();
      if (columns) {
        setReportTableData([]);
        setActTableData([]);
        setTableColumn(columns);
        constructTableData(columns, data);
      }
    }
  }, [data, meta]);

  const refreshTable = (personalization, pageCondition) => {
    if (pageCondition) {
      setTableLoading(true);
      if (source) {
        getReportData(true, {
          ...listInfo,
          page: pageCondition.page || 1,
          pageSize: pageCondition.pageSize || ROW_COUNT
        });
        return;
      }
      getReportData({
        ...listInfo,
        page: pageCondition.page || 1,
        pageSize: pageCondition.pageSize || ROW_COUNT
      });
    }
  };

  if (loading) {
    return <SkeletonTableLoading />;
  }

  return (
    <Wrapper className="fw">
      {isGroupBy ? (
        <ExpandableTableComponent
          data={actTableData}
          isLoading={tableLoading}
          columns={tableColumn}
          listInfo={listInfo}
          pagination={Math.ceil(listInfo.totalCount / listInfo.pageSize) > 1}
          refreshTable={refreshTable}
        />
      ) : (
        <TableContainer className="scroll dataReportTable">
          <SuperTable
            isLoading={tableLoading}
            noBulkSelect
            columns={columnGroup}
            listInfo={listInfo}
            defaultColumnCells={defaultColumnCells}
            context={{
              records: reportTableData
            }}
            data={reportTableData || []}
            rowCount={ROW_COUNT}
            tableName={tableName}
            refreshTable={refreshTable}
            fullHeight
            pagination={Math.ceil(listInfo.totalCount / listInfo.pageSize) > 1}
            pages={Math.ceil(listInfo.totalCount / listInfo.pageSize) || 0}
            activePage={listInfo.page || 0}
            recordCount={listInfo?.totalCount || 0}
            getAttributeName={getAttributeName}
            padded
            dynamicRowCount={false}
          />
        </TableContainer>
      )}
    </Wrapper>
  );
}

export default DataReportTable;
